import { EditorState, Plugin, PluginKey } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { ProsemirrorDispatch, RtPMPlugin } from '../../editor/types';
import { getToolbarPosition } from '../../helpers/position';
import { ContextMenuState, ContextMenuController } from './types';

const key = new PluginKey<ContextMenuState>('contextMenu');

export const contextMenuCommand = {
  open: (state: EditorState, dispatch: ProsemirrorDispatch, force: boolean) => {
    dispatch(
      state.tr.setMeta(key, {
        isContextMenuOpen: true,
        isContextMenuForcedState: force,
      }),
    );
  },
  close: (state: EditorState, dispatch: ProsemirrorDispatch, force: boolean) => {
    dispatch(
      state.tr.setMeta(key, {
        isContextMenuOpen: false,
        isContextMenuForcedState: force,
      }),
    );
  },
  disableForcedState: (state: EditorState, dispatch: ProsemirrorDispatch) => {
    dispatch(
      state.tr.setMeta(key, {
        isContextMenuOpen: false,
        isContextMenuForcedState: false,
      }),
    );
  },
};

export class ContextMenuPlugin implements RtPMPlugin {
  public static getState(state: EditorState) {
    return key.getState(state);
  }

  private viewUpdated$ = new Subject();
  private destroy$ = new Subject();
  private editorView: EditorView;
  private plugin = new Plugin({
    key,
    state: {
      init: (): ContextMenuState => ({
        isContextMenuOpen: false,
        isContextMenuForcedState: false,
      }),
      apply: (tr, value, oldState, newState) => {
        const data = tr.getMeta(key);
        if (!data) return value;

        return data;
      },
    },
    view: () => ({
      update: (view, prevState) => this.viewUpdated$.next(),
    }),
    filterTransaction: (transaction, editorState) => {
      const currentState = ContextMenuPlugin.getState(editorState)!;
      const nextState = <ContextMenuState>transaction.getMeta(key);

      if (!nextState || !nextState.isContextMenuForcedState) return true;

      if (!currentState.isContextMenuOpen && nextState.isContextMenuOpen) {
        this.open(this.editorView);
      }

      if (currentState.isContextMenuOpen && !nextState.isContextMenuOpen) {
        this.options.close();
      }

      return true;
    },
  });

  constructor(private options: ContextMenuController, private scroll$: Observable<void>) {}

  public afterEditorInit(editorView: EditorView): void {
    this.editorView = editorView;

    this.scroll$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.options.isOpen()) return;

      this.updatePosition(editorView);
    });

    fromEvent(document, 'click')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (!this.options.isOpen()) return;

        const contextMenuElement = this.options.getElement();
        let insideEditorClick = false;
        let node: HTMLElement | null = <HTMLElement>event.target;
        while (node) {
          if (node === this.editorView.dom || node === contextMenuElement) {
            insideEditorClick = true;
          }
          node = node.parentElement;
        }

        if (!insideEditorClick) {
          this.options.close();
        }
      });

    this.viewUpdated$.pipe(debounceTime(0), takeUntil(this.destroy$)).subscribe(() => {
      const view = this.editorView;
      const { from, to, empty } = view.state.selection;
      const { isContextMenuOpen, isContextMenuForcedState } = ContextMenuPlugin.getState(
        view.state,
      )!;

      const hasTextSelected = this.editorView.state.doc.textBetween(from, to);

      const canOpen = from !== to && hasTextSelected;
      const canClose = from === to || empty || !hasTextSelected;

      if (isContextMenuOpen && from !== to) {
        return this.updatePosition(view);
      }

      if (isContextMenuForcedState) return;

      if (canClose && isContextMenuOpen) {
        this.close(view);
      } else if (canOpen && !isContextMenuOpen) {
        this.open(view);
        return contextMenuCommand.open(view.state, view.dispatch, false);
      }
    });
  }

  public destroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getNativeEditorPlugins() {
    return [this.plugin];
  }

  private open(view: EditorView) {
    const selectionRectangle = getToolbarPosition(view);
    this.options.open(selectionRectangle);
  }

  private close(view: EditorView) {
    this.options.close();
    contextMenuCommand.close(view.state, view.dispatch, false);
  }

  private updatePosition(view: EditorView) {
    const selectionRectangle = getToolbarPosition(view);
    this.options.updatePosition(selectionRectangle);
  }
}
